import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Siren, BellOff, ArrowRight, Volume2 } from "lucide-react";
import { useChat } from "@/components/chat/ChatProvider";
import { useInbox } from "@/components/inbox/InboxProvider";
import { unlockAudio, audioReady, playSiren } from "@/lib/siren";

const ALARM_MIN = 20;
const SNOOZE_MS = 5 * 60 * 1000;
const SNOOZE_KEY = "crm_alarm_snooze";
const WORK_MS = 2 * 60 * 1000;
const spNow = () => { const [h, m] = new Date().toLocaleTimeString("en-GB", { timeZone: "America/Sao_Paulo", hour12: false }).split(":").map(Number); return h * 60 + m; };
// alarm hours: 09:30-11:59 and 13:30-22:00 (30 min grace after opening and lunch)
export const alarmHours = () => { const t = spNow(); return (t >= 570 && t < 720) || (t >= 810 && t <= 1320); };
const mins = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);

const useWaiting = () => {
  const { items: chats } = useChat();
  const { items: threads } = useInbox();
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 15000); return () => clearInterval(t); }, []);
  const chat = chats.filter((s) => s.status === "open" && s.waiting_since && mins(s.waiting_since) >= ALARM_MIN)
    .map((s) => ({ key: `c:${s.id}:${s.waiting_since}`, where: "Chat do site", name: s.name || "Visitante", min: mins(s.waiting_since), to: `/chat?s=${s.id}` }));
  const fin = threads.filter((t) => mins(t.open_since || t.last_at) >= ALARM_MIN)
    .map((t) => ({ key: `f:${t.wa_id}:${t.open_since || t.last_at}`, where: "Financeiro (WhatsApp)", name: t.client?.name || t.phone, min: mins(t.open_since || t.last_at), to: "/respostas" }));
  return alarmHours() ? [...chat, ...fin].sort((a, b) => b.min - a.min) : [];
};

const readSnooze = () => { try { return JSON.parse(localStorage.getItem(SNOOZE_KEY)) || {}; } catch { return {}; } };

export const WaitAlarm = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const all = useWaiting();
  const [sound, setSound] = useState(false);
  const [snooze, setSnooze] = useState(readSnooze);
  const notified = useRef(new Set());
  const active = useMemo(() => all.filter((w) => !(snooze.until > Date.now() && snooze.keys?.includes(w.key))), [all, snooze]);
  const [workUntil, setWorkUntil] = useState(0);
  const [, tick] = useState(0);
  const working = workUntil > Date.now() && /^\/(chat|respostas)/.test(loc.pathname);
  const here = (w) => `${loc.pathname}${loc.search}` === w.to || (w.to === "/respostas" && loc.pathname === "/respostas");
  const loud = working ? [] : active.filter((w) => !here(w));
  const on = loud.length > 0;

  useEffect(() => {
    const unlock = () => {
      unlockAudio(); setSound(audioReady());
      if (/^\/(chat|respostas)/.test(window.location.pathname)) setWorkUntil(Date.now() + WORK_MS);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    const sync = (e) => e.key === SNOOZE_KEY && setSnooze(readSnooze());
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); window.removeEventListener("storage", sync); };
  }, []);

  useEffect(() => {
    if (!workUntil) return;
    const t = setTimeout(() => tick((x) => x + 1), Math.max(0, workUntil - Date.now()) + 300);
    return () => clearTimeout(t);
  }, [workUntil]);

  useEffect(() => {
    if (!snooze.until) return;
    const t = setTimeout(() => setSnooze({}), Math.max(0, snooze.until - Date.now()) + 500);
    return () => clearTimeout(t);
  }, [snooze]);

  useEffect(() => {
    if (!on) return;
    playSiren();
    const t = setInterval(playSiren, 4000);
    const title = document.title;
    let flip = false;
    const b = setInterval(() => { flip = !flip; document.title = flip ? `(!) CLIENTE ESPERANDO ${loud[0].min} MIN` : title; }, 900);
    loud.forEach((w) => {
      if (notified.current.has(w.key) || !("Notification" in window) || Notification.permission !== "granted") return;
      notified.current.add(w.key);
      try { new Notification(`Cliente esperando há ${w.min} min`, { body: `${w.name} · ${w.where}`, tag: w.key, requireInteraction: true }); } catch { /* ignore */ }
    });
    return () => { clearInterval(t); clearInterval(b); document.title = title; };
  }, [on, on && loud[0].key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!on) {
    if (active.length) return (
      <div data-testid="wait-alarm-banner" className="fixed left-1/2 top-3 z-[200] -translate-x-1/2 rounded-full border border-red-400 bg-red-600 px-4 py-1.5 text-xs font-bold text-white shadow-lg">
        {active.length > 1 ? `${active.length} clientes esperando (o mais antigo há ${active[0].min} min)` : `Cliente esperando há ${active[0].min} min`} · responda para parar o alarme
      </div>);
    return sound || audioReady() ? null : (
      <button data-testid="wait-alarm-enable-sound" onClick={() => { unlockAudio(); setTimeout(() => setSound(audioReady()), 150); }}
        className="fixed left-1/2 top-3 z-[150] inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-amber-400/50 bg-[#1A1408] px-3 py-1.5 text-[11px] font-semibold text-amber-200 shadow-lg">
        <Volume2 className="h-3.5 w-3.5" /> Clique para ativar o som do alarme
      </button>);
  }
  const top = loud[0];
  const doSnooze = () => {
    const s = { until: Date.now() + SNOOZE_MS, keys: all.map((w) => w.key) };
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(s));
    setSnooze(s);
  };
  return (
    <div data-testid="wait-alarm" className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="alarm-flash absolute inset-0" />
      <div className="relative w-full max-w-lg rounded-3xl border-2 border-red-400 bg-[#14070A]/95 p-7 text-center shadow-[0_0_80px_rgba(239,68,68,0.6)] backdrop-blur-xl">
        <Siren className="alarm-shake mx-auto h-16 w-16 text-red-400" />
        <div data-testid="wait-alarm-title" className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">CLIENTE ESPERANDO HÁ {top.min} MIN</div>
        <div data-testid="wait-alarm-who" className="mt-2 text-base text-red-100"><b>{top.name}</b> · {top.where}</div>
        {loud.length > 1 && <div data-testid="wait-alarm-more" className="mt-1 text-sm text-red-200/80">e mais {loud.length - 1} cliente(s) aguardando há mais de {ALARM_MIN} min</div>}
        {!audioReady() && (
          <button data-testid="wait-alarm-sound" onClick={() => { unlockAudio(); setTimeout(() => { setSound(true); playSiren(); }, 150); }}
            className="mx-auto mt-4 inline-flex items-center gap-2 rounded-lg border border-amber-400/50 px-3 py-1.5 text-xs font-semibold text-amber-200">
            <Volume2 className="h-4 w-4" /> Ativar som do alarme
          </button>)}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button data-testid="wait-alarm-go" onClick={() => { unlockAudio(); setWorkUntil(Date.now() + WORK_MS); nav(top.to); }}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-500 py-3.5 text-base font-extrabold text-white transition-transform hover:-translate-y-0.5">
            Atender agora <ArrowRight className="h-5 w-5" />
          </button>
          <button data-testid="wait-alarm-snooze" onClick={doSnooze}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-400/50 px-4 py-3.5 text-sm font-semibold text-red-100 hover:bg-red-500/10">
            <BellOff className="h-4 w-4" /> Silenciar 5 min
          </button>
        </div>
        <div className="mt-3 text-[11px] text-red-200/60">O alarme só para quando o cliente for respondido.</div>
      </div>
    </div>
  );
};
